import {action, computed, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import type {IJSONProvider} from "data/providers/json/json.provider";
import {Bindings} from "data/constants/bindings";
import {PlayerStatus, TeamRegion} from "data/enums";
import {chain} from "lodash";

type PinpointByRound = Record<number | string, PinpointByHole>;

export type PinpointByHole = {
	distance: number;
	distanceToPin: number;
	holePar: number;
};

export type PinpointByHoleWithPlayerId = PinpointByHole & {playerId: number};

export interface IPlayer {
	id: number;
	fullName: string;
	firstName: string;
	lastName: string;
	status: PlayerStatus;
	country: string;
	team: TeamRegion;
	worldRanking: number | null;
	stats: IPlayerStats;
	isTop: boolean;
	pinpoint: Record<number | string, PinpointByRound>;
}

export interface IRecords {
	wins: number;
	losses: number;
	draws: number;
}

export interface IPlayerStats {
	RCAppearances: number;
	RCEURank: number;
	RCEUWorldRank: number;
	RCUSRank: number;
	allTimeRecords: IRecords;
	seasonRecords: IRecords;
	avgPoints: number;
	pickedBy: number;
}

export interface IPlayersStore {
	get getIsLoading(): boolean;

	get list(): IPlayer[];

	get availablePlayersList(): IPlayer[];

	get getUsaPlayers(): IPlayer[];

	get getEuropePlayers(): IPlayer[];

	get groupPlayersByRound(): Record<string, Record<string, PinpointByHoleWithPlayerId[]>>;

	getLongestDrive: (roundId: number, hole: number) => PinpointByHoleWithPlayerId | undefined;

	getNearestPin: (roundId: number, hole: number) => PinpointByHoleWithPlayerId | undefined;

	getPlayerById(playerId: number): IPlayer | undefined;

	setPlayerIdForInfoModal: (playerId: number) => void;

	fetchPlayers(): Promise<void>;
}

@injectable()
export class PlayersStore implements IPlayersStore {
	@observable private _isLoading: boolean = false;
	@observable private _playerId: number = 0;

	constructor(@inject(Bindings.JSONProvider) private _jsonProvider: IJSONProvider) {
		makeAutoObservable(this);
	}

	@observable private _list: IPlayer[] = [];

	get list() {
		return this._list;
	}

	get getIsLoading(): boolean {
		return this._isLoading;
	}

	@computed get availablePlayersList() {
		return chain(this._list)
			.filter((it) => it.status === PlayerStatus.Available)
			.orderBy("worldRanking", "asc")
			.value();
	}

	@computed get getUsaPlayers() {
		return chain(this.availablePlayersList)
			.filter((it) => it.team === TeamRegion.Usa)
			.orderBy("stats.RCUSRank", "asc")
			.value();
	}

	@computed get getEuropePlayers() {
		return this.availablePlayersList.filter((it) => it.team === TeamRegion.Europe);
	}

	getPlayerById(playerId: number): IPlayer | undefined {
		return this._list.find((player) => player.id === playerId);
	}

	@computed get groupPlayersByRound() {
		const groupedData = {} as Record<string, Record<string, PinpointByHoleWithPlayerId[]>>;

		this.list.forEach((player) => {
			for (const roundId in player.pinpoint) {
				if (!groupedData[roundId]) {
					groupedData[roundId] = {};
				}

				for (const hole in player.pinpoint[roundId]) {
					if (!groupedData[roundId][hole]) {
						groupedData[roundId][hole] = [];
					}

					groupedData[roundId][hole].push({
						playerId: player.id,
						distance: player.pinpoint[roundId][hole].distance,
						holePar: player.pinpoint[roundId][hole].holePar,
						distanceToPin: player.pinpoint[roundId][hole].distanceToPin,
					});
				}
			}
		});

		return groupedData;
	}

	getLongestDrive = (roundId: number, hole: number) => {
		const players = this.groupPlayersByRound[roundId]?.[hole];

		if (!players?.length) {
			return undefined;
		}

		const sortedPlayers = chain(players)
			.filter((player) => player.distance !== null)
			.orderBy("distance", "desc")
			.value();
		return sortedPlayers[0];
	};

	getNearestPin = (roundId: number, hole: number) => {
		const players = this.groupPlayersByRound[roundId]?.[hole];

		if (!players?.length) {
			return undefined;
		}

		const sortedPlayers = chain(players)
			.filter((player) => player.distanceToPin !== null)
			.orderBy("distanceToPin", "asc")
			.value();
		return sortedPlayers[0];
	};

	@action setPlayerIdForInfoModal = (playerId: number) => {
		this._playerId = playerId;
	};

	@action
	async fetchPlayers() {
		const {data} = await this._jsonProvider.players();

		runInAction(() => {
			this._list = data;
		});
	}
}
